import {
  createContext,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "../lib/utils.js";
import { useLayer } from "../primitives/useLayer.js";
import { useDismissable } from "../primitives/useDismissable.js";
import { useFocusTrap } from "../primitives/useFocusTrap.js";
import { getLayerRoot } from "../primitives/layer-stack.js";
import { useTransitionPresence } from "../hooks/useTransitionPresence.js";
import { MOTION_DURATION_MS } from "../motion.js";
import {
  ExpandedCardModalHeader,
  type ExpandedCardModalDensity,
  type ExpandedCardModalHeaderVariant,
  type ExpandedCardModalTitleSize,
} from "./ExpandedCardModalHeader.js";

/** Stable empty array — `outsidePress` is off, so no element needs to count as inside. */
const EMPTY_INSIDE_REFS: ReadonlyArray<React.RefObject<Element | null>> = [];

export interface ExpandedCardModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title: string;
  /** Optional content rendered to the right of the title in the header. */
  readonly headerRight?: ReactNode;
  /**
   * Optional actions rendered at the TRAILING end of the header, grouped with
   * Close and immediately before it.
   *
   * Distinct from `headerRight`, which is CENTRED and holds a panel's
   * contextual controls (view tabs, a jump-to link) — content that belongs
   * beside the title and stacks under it on a narrow viewport. This slot is for
   * per-panel actions that read as part of the panel's own chrome, so they sit
   * where the reader already looks for chrome. Both may be supplied at once;
   * neither displaces the other.
   */
  readonly headerActionsSlot?: ReactNode;
  /** Optional icon rendered to the left of the title in the header. */
  readonly titleIcon?: ReactNode;
  /**
   * Optional small control rendered immediately AFTER the title text, in the
   * title row and vertically centred with it — an info button, say. It is a
   * sibling of the title span, never inside it, so the dialog's accessible name
   * stays the title alone, and it follows the title in the DOM, so Tab reaches
   * it right after the title. It is not part of the subtitle line, which clips
   * its overflow.
   */
  readonly titleAccessory?: ReactNode;
  /**
   * Optional content rendered as a sibling BEFORE the title block — a Back
   * button, for instance. It sits outside the title span, so it never joins
   * the dialog's accessible name, which stays the title alone.
   */
  readonly leading?: ReactNode;
  /**
   * Layout-only classes on the title span: grid or flex placement, overflow,
   * `min-h-0`, cursor. Padding, surface paint and typography through it are
   * deprecated: the title's type size goes to `titleSize`.
   */
  readonly titleClassName?: string;
  /**
   * Optional one-line subtitle under the title. When present the header renders
   * the design's title block — `titleIcon` as a leading chip beside a
   * title + subtitle stack — instead of the inline icon + title span. The
   * dialog's accessible name stays the title alone.
   */
  readonly subtitle?: ReactNode;
  /**
   * Layout-only classes on the header row: grid or flex placement, overflow,
   * `min-h-0`, cursor. Padding, surface paint and typography through it are
   * deprecated: header padding goes to `density`, the header wash to
   * `headerVariant` and the title's type size to `titleSize`.
   */
  readonly headerClassName?: string;
  /**
   * Layout-only classes on the body wrapper: grid or flex placement,
   * overflow, `min-h-0`, cursor. Padding, surface paint and typography
   * through it are deprecated: body padding goes to `bodyPadding`.
   */
  readonly bodyClassName?: string;
  /**
   * Header spacing. `comfortable` (default) is the `px-5 py-3` row with a
   * 12px subtitle; `compact` is a 54px minimum row, `px-4 py-2.5`, with a
   * 13px subtitle.
   */
  readonly density?: ExpandedCardModalDensity;
  /**
   * Body padding. `default` is `p-5`; `none` lets full-bleed sections and
   * their dividers run edge to edge — each section pads itself.
   */
  readonly bodyPadding?: ExpandedCardModalBodyPadding;
  /**
   * Header surface. `default` paints nothing; `elevated` lays a
   * `pb-card-elevated` wash on the header row, solid under high contrast.
   */
  readonly headerVariant?: ExpandedCardModalHeaderVariant;
  /** Title size. `md` (default) is 14px semibold; `lg` is `text-heading-3`. */
  readonly titleSize?: ExpandedCardModalTitleSize;
  /**
   * Extra classes on the panel itself, for a surface treatment a consumer's
   * design calls for and the default does not carry — `pb-hairline`, say.
   * Opt-in on purpose: the default surface is shared by every expanded card,
   * so one consumer's treatment must not reach the rest.
   */
  readonly panelClassName?: string;
  /**
   * `full` is the default full-card workspace, `lg` keeps focused forms
   * compact, and `wide` is the design's expanded artifact panel: at most
   * 1240px wide, at one fixed height whatever the body holds.
   */
  readonly size?: ExpandedCardModalSize;
  /**
   * Trap and restore focus, lock scrolling, and make content outside the panel
   * inert. Use for draft-bearing forms; ordinary expanded cards stay non-modal.
   */
  readonly modal?: boolean;
  /** Move focus to the visible title on open without trapping keyboard focus. */
  readonly focusOnOpen?: boolean;
  /**
   * Keep the visual backdrop while allowing pointer interaction with the
   * workspace behind it. The panel itself remains interactive. Use only with
   * a non-modal panel whose surrounding workspace must stay operable.
   */
  readonly allowOutsideInteraction?: boolean;
  readonly children: ReactNode;
}

type ExpandedCardModalSize = "full" | "lg" | "wide";

/** Body padding: `default` is today's `p-5`, `none` runs sections edge to edge. */
export type ExpandedCardModalBodyPadding = "default" | "none";

const BODY_PADDING: Record<ExpandedCardModalBodyPadding, string> = {
  default: "p-5",
  none: "p-0",
};

const MODAL_SIZE_CLASSES: Record<
  ExpandedCardModalSize,
  { readonly bounded: string; readonly viewport: string }
> = {
  full: {
    bounded: "h-[96%] w-[96%]",
    viewport: "h-[84vh] w-[92vw]",
  },
  lg: {
    bounded:
      "h-[min(48rem,calc(100%-1.5rem))] w-[min(48rem,calc(100%-1.5rem))]",
    viewport:
      "h-[min(48rem,calc(100dvh-2rem))] w-[min(48rem,calc(100vw-2rem))]",
  },
  // A FIXED height on purpose: the panel is the same size whether the body
  // holds no rows, one or twenty, and a long body scrolls inside it — a panel
  // that grew with its content would jump between opens. 77.5rem is 1240px.
  wide: {
    bounded: "h-[96%] w-[min(77.5rem,96%)]",
    viewport: "h-[94vh] w-[min(77.5rem,96vw)]",
  },
};

/**
 * Optional host element for bounded expansion. When non-null, modal sizes to
 * the host's viewport rect. When null (default), falls back to a full-viewport
 * modal. SOLVE wires the canvas region as the host so the app top bar + right
 * rail stay clickable.
 */
const ExpandedCardHostContext = createContext<HTMLElement | null>(null);

export function ExpandedCardHostProvider({
  host,
  children,
}: {
  host: HTMLElement | null;
  children: ReactNode;
}): JSX.Element {
  return (
    <ExpandedCardHostContext.Provider value={host}>
      {children}
    </ExpandedCardHostContext.Provider>
  );
}

export function useExpandedCardHost(): HTMLElement | null {
  return useContext(ExpandedCardHostContext);
}

/**
 * Full-card expanded view. Opened from `ModuleCard.onExpand`. Closed via
 * the X button, Escape key, or backdrop click. Dispatches global window
 * events `pb:open-expanded-card` / `pb:close-expanded-card` so sibling
 * elements (e.g. phase nav) can hide chrome when a card is focused.
 */
export function ExpandedCardModal({
  open,
  onClose,
  title,
  headerRight,
  headerActionsSlot,
  titleIcon,
  titleAccessory,
  leading,
  titleClassName,
  subtitle,
  headerClassName,
  bodyClassName,
  density = "comfortable",
  bodyPadding = "default",
  headerVariant = "default",
  titleSize = "md",
  panelClassName,
  size = "full",
  modal = false,
  focusOnOpen = false,
  allowOutsideInteraction = false,
  children,
}: ExpandedCardModalProps): JSX.Element | null {
  const host = useExpandedCardHost();
  const prevOpenRef = useRef(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLSpanElement>(null);
  const [hostRect, setHostRect] = useState<HostRect | null>(null);

  useEffect(() => {
    if (open && !prevOpenRef.current) {
      window.dispatchEvent(new Event("pb:open-expanded-card"));
    } else if (!open && prevOpenRef.current) {
      window.dispatchEvent(new Event("pb:close-expanded-card"));
    }
    prevOpenRef.current = open;
  }, [open]);

  // Join the shared layer stack so Escape unwinds ONE overlay per press.
  //
  // This previously used its own unconditional `window` keydown listener, which put it
  // outside the overlay engine entirely. Every other overlay dismisses through
  // `useDismissable`, which listens on `document` with a top-layer check — and
  // document-phase fires before window-phase while `preventDefault()` does not stop
  // propagation, so a single Escape closed BOTH a nested overlay and this panel. That is
  // reachable wherever an expanded body opens a DS Modal (the VSM preparation bodies do,
  // via SourcePicker and the upload dialog).
  //
  // Ordinary expanded cards deliberately remain non-modal so adjacent workspace chrome
  // stays usable. Draft-bearing forms opt into the modal contract: the same bounded
  // geometry, plus focus containment/return, scroll lock, and an inert background.
  //
  // `outsidePress: false` keeps dismissal behaviour exactly as it was: this panel has
  // never closed on an outside press, and this change is about Escape ordering only.
  const layerId = useLayer({ open, modal });
  useDismissable({
    layerId,
    open,
    onDismiss: onClose,
    insideRefs: EMPTY_INSIDE_REFS,
    escape: true,
    outsidePress: false,
  });
  useFocusTrap(panelRef, { active: open && modal, layerId });
  const { present, phase } = useTransitionPresence(
    open,
    MOTION_DURATION_MS.state,
  );
  const closing = phase === "closing";
  useLayoutEffect(() => {
    panelRef.current?.toggleAttribute("inert", closing);
  }, [closing, present]);
  useEffect(() => {
    if (!open || modal || !focusOnOpen) return undefined;
    const focusTimer = window.setTimeout(() => titleRef.current?.focus(), 0);
    return () => window.clearTimeout(focusTimer);
  }, [focusOnOpen, modal, open]);

  // Declared here, above the early returns, because hooks must run unconditionally.
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const onCloseEvent = (): void => onClose();
    window.addEventListener("pb:close-expanded-card", onCloseEvent);
    return (): void => {
      window.removeEventListener("pb:close-expanded-card", onCloseEvent);
    };
  }, [open, onClose]);

  useLayoutEffect(() => {
    // No setHostRect(null) on close/no-host: the render below only reads
    // hostRect when `host` is truthy (and falls back to a fresh readHostRect
    // there), and returns null / the full-viewport fallback otherwise — so a
    // stale rect is never shown.
    if (!open || !host) return undefined;

    const updateHostRect = (): void => setHostRect(readHostRect(host));
    updateHostRect();

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(updateHostRect);
    resizeObserver?.observe(host);
    window.addEventListener("resize", updateHostRect);
    window.addEventListener("scroll", updateHostRect, true);

    return (): void => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateHostRect);
      window.removeEventListener("scroll", updateHostRect, true);
    };
  }, [host, open]);

  /* Held for its closing frame (ADR-0091). Everything LOGICAL still follows
     `open` — the layer, the dismiss engine and the focus trap above — so a
     closing card owns nothing; only the paint lingers. */
  if (!present) return null;
  if (typeof document === "undefined") return null;
  const portalTarget = modal ? getLayerRoot() : document.body;
  const outsideInteractive = allowOutsideInteraction && !modal;
  const content = (
    <>
      <ExpandedCardModalHeader
        title={title}
        titleId={titleId}
        onClose={onClose}
        headerRight={headerRight}
        headerActionsSlot={headerActionsSlot}
        titleIcon={titleIcon}
        titleAccessory={titleAccessory}
        leading={leading}
        titleClassName={titleClassName}
        subtitle={subtitle}
        headerClassName={headerClassName}
        density={density}
        headerVariant={headerVariant}
        titleSize={titleSize}
        titleRef={titleRef}
        focusableTitle={focusOnOpen && !modal}
      />
      <div
        className={cn(
          "flex-1 min-h-0 flex flex-col",
          BODY_PADDING[bodyPadding],
          bodyClassName,
        )}
      >
        {children}
      </div>
    </>
  );

  // Bounded mode: panel is fixed to the canvas viewport rect. Rendering
  // through document.body keeps hidden canvas scroll offsets from moving the
  // modal when focus lands on a lower widget before expansion.
  if (host) {
    const rect = hostRect ?? readHostRect(host);
    return createPortal(
      <div
        data-pbds-layer-id={layerId}
        data-pbds-modal-layer={modal ? "" : undefined}
        className={cn(
          "fixed z-40 flex items-center justify-center p-3",
          /* While it fades the scrim is invisible but still covers the canvas,
             and it still carries onClose. Without this, a click aimed at a
             widget behind a just-closed card lands on the scrim instead and is
             silently lost (ADR-0091). ModalShell already does this. */
          (outsideInteractive || closing) && "pointer-events-none",
        )}
        style={{
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
        }}
      >
        <div
          aria-hidden
          data-motion-state={phase}
          className="pb-motion-scrim absolute inset-0 bg-pb-overlay/70 backdrop-blur-sm motion-reduce:transition-none"
          onClick={outsideInteractive ? undefined : onClose}
        />
        <div
          ref={panelRef}
          role="dialog"
          data-motion-state={phase}
          {...(closing ? { "aria-hidden": "true" as const } : {})}
          aria-labelledby={titleId}
          aria-modal={modal ? "true" : undefined}
          className={cn(
            "pb-widget pb-motion-overlay pb-motion-origin-dialog relative z-10 bg-pb-card rounded-xl border border-pb-border shadow-2xl flex flex-col overflow-hidden",
            outsideInteractive && "pointer-events-auto",
            MODAL_SIZE_CLASSES[size].bounded,
            panelClassName,
          )}
        >
          {content}
        </div>
      </div>,
      portalTarget,
    );
  }

  // Full-viewport fallback.
  return createPortal(
    <div
      data-pbds-layer-id={layerId}
      data-pbds-modal-layer={modal ? "" : undefined}
      className={cn(
        "fixed inset-0 z-50 flex items-center justify-center p-6",
        // Same reason as the bounded branch above.
        (outsideInteractive || closing) && "pointer-events-none",
      )}
    >
      <div
        aria-hidden
        data-motion-state={phase}
        className="pb-motion-scrim absolute inset-0 bg-pb-overlay/70 backdrop-blur-sm motion-reduce:transition-none"
        onClick={outsideInteractive ? undefined : onClose}
      />
      <div
        ref={panelRef}
        role="dialog"
        data-motion-state={phase}
        {...(closing ? { "aria-hidden": "true" as const } : {})}
        aria-labelledby={titleId}
        aria-modal={modal ? "true" : undefined}
        className={cn(
          "pb-widget pb-motion-overlay pb-motion-origin-dialog relative z-10 bg-pb-card rounded-xl border border-pb-border shadow-2xl flex flex-col overflow-hidden",
          outsideInteractive && "pointer-events-auto",
          MODAL_SIZE_CLASSES[size].viewport,
          panelClassName,
        )}
      >
        {content}
      </div>
    </div>,
    portalTarget,
  );
}

interface HostRect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

function readHostRect(host: HTMLElement): HostRect {
  const rect = host.getBoundingClientRect();
  return {
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height,
  };
}
